import { useState } from 'react';
import type { Material } from '@lms/shared/types';
import { FileText, Download, ExternalLink, AlertCircle, Video } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';

interface MediaPlayerProps {
  material: Material;
}

export function MediaPlayer({ material }: MediaPlayerProps) {
  const [hasError, setHasError] = useState(false);
  const [activeTab, setActiveTab] = useState<'VIDEO' | 'PDF'>('VIDEO');
  const { t } = useLanguage();

  const videoUrl =
    material.mediaUrl ||
    (material.type === 'VIDEO' ? material.content : null) ||
    material.media?.find(m => m.type === 'VIDEO')?.url;

  const pdfUrl =
    material.pdfUrl ||
    (material.type === 'DOCUMENT' ? material.content : null) ||
    material.media?.find(m => m.type === 'PDF')?.url;

  const hasBoth = !!(videoUrl && pdfUrl);

  const mediaUrl =
    activeTab === 'PDF' && pdfUrl
      ? pdfUrl
      : videoUrl || pdfUrl || material.content || material.media?.[0]?.url;

  if (!mediaUrl) {
    return (
      <div className="aspect-video bg-surface-container flex flex-col items-center justify-center p-6 text-center border border-on-surface/5">
        <AlertCircle size={40} className="text-warning mb-4" />
        <h3 className="font-headline font-bold text-on-surface mb-2">
          {t(copy.mediaPlayer.noMedia)}
        </h3>
        <p className="text-sm text-on-surface-variant max-w-md">
          {t(copy.mediaPlayer.noMediaDesc)}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      {/* Dual Tab Switcher if both Video & PDF exist */}
      {hasBoth && (
        <div className="flex items-center gap-2 p-2 bg-surface-container-high/40 border-b border-on-surface/5">
          <button
            onClick={() => setActiveTab('VIDEO')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === 'VIDEO'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            <Video size={14} />
            <span>Video Pembelajaran</span>
          </button>
          <button
            onClick={() => setActiveTab('PDF')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === 'PDF'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            <FileText size={14} />
            <span>Modul / PDF Kitab</span>
          </button>
        </div>
      )}

      {/* --- VIDEO EMBED OR PLAYER --- */}
      {activeTab === 'VIDEO' && videoUrl ? (
        (() => {
          const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
          const isGoogleDrive = videoUrl.includes('drive.google.com');

          if (isYouTube) {
            let videoId = '';
            if (videoUrl.includes('v=')) {
              videoId = videoUrl.split('v=')[1]?.split('&')[0];
            } else if (videoUrl.includes('youtu.be/')) {
              videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
            }
            if (videoId) {
              return (
                <div className="relative w-full aspect-video rounded-b-[2rem] overflow-hidden shadow-level2 bg-black">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                    title={material.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }
          }

          if (isGoogleDrive) {
            const drivePreviewUrl = videoUrl
              .replace(/\/view(\?.*)?$/, '/preview')
              .replace(/\/edit(\?.*)?$/, '/preview');
            return (
              <div className="relative w-full aspect-video rounded-b-[2rem] overflow-hidden shadow-level2 bg-black">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={drivePreviewUrl}
                  title={material.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            );
          }

          return (
            <div className="relative w-full aspect-video rounded-b-[2rem] overflow-hidden shadow-level2 bg-surface-container-lowest border border-on-surface/5 group">
              {hasError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-surface-container text-on-surface-variant p-6 text-center">
                  <AlertCircle size={40} className="text-error mb-4" />
                  <p className="font-bold mb-1">{t(copy.mediaPlayer.loadFailed)}</p>
                  <p className="text-xs">{t(copy.mediaPlayer.loadFailedDesc)}</p>
                </div>
              ) : (
                <video
                  className="w-full h-full object-contain bg-black"
                  controls
                  controlsList="nodownload"
                  preload="metadata"
                  src={videoUrl}
                  onError={() => setHasError(true)}
                  poster={`https://images.unsplash.com/photo-1620325867502-221ddb5b4f24?auto=format&fit=crop&w=1200&q=80`}
                >
                  {t(copy.mediaPlayer.browserNotSupportVideo)}
                </video>
              )}
            </div>
          );
        })()
      ) : (
        /* --- DOCUMENT VIEWER (PDF) --- */
        <div className="w-full rounded-b-[2rem] overflow-hidden shadow-level1 bg-surface-container-lowest border border-on-surface/5">
          <div className="p-4 md:p-6 border-b border-on-surface/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-primary/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 text-primary rounded-xl">
                <FileText size={22} />
              </div>
              <div>
                <h3 className="font-headline font-bold text-on-surface text-base">
                  {t(copy.mediaPlayer.docResource)}
                </h3>
                <p className="text-[11px] text-on-surface-variant">
                  {t(copy.mediaPlayer.docResourceDesc)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors no-underline"
              >
                <ExternalLink size={14} /> {t(copy.mediaPlayer.openNewTab)}
              </a>
              <a
                href={mediaUrl}
                download
                className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-on-primary bg-primary hover:bg-primary-container hover:text-on-primary-container rounded-xl transition-colors shadow-sm no-underline"
              >
                <Download size={14} /> {t(copy.mediaPlayer.download)}
              </a>
            </div>
          </div>

          <div className="w-full bg-surface-container-low relative min-h-[500px] md:min-h-[750px]">
            {hasError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-on-surface-variant">
                <FileText size={48} className="mb-4 opacity-50" />
                <p className="font-bold text-on-surface mb-2">
                  {t(copy.mediaPlayer.previewNotAvailable)}
                </p>
                <p className="text-sm max-w-sm mb-6">
                  {t(copy.mediaPlayer.previewNotAvailableDesc)}
                </p>
              </div>
            ) : (
              <iframe
                src={`${mediaUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                className="w-full h-[500px] md:h-[750px] border-none"
                title={material.title}
                onError={() => setHasError(true)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
